import { useEffect } from 'react'
import {
    GET_IP_ADDRESS_URL,
    IMAGES_DOMAIN,
    MESSAGES,
    giftLicenseDetails,
} from '../constants'
import moment from 'moment'
import { Result } from '../types/Split'
import { State } from 'country-state-city'
import { IState } from 'country-state-city/lib/interface'
import {
    AUTHENTICATED_USER,
    CARD_TYPE,
    VIDEO_CAROUSEL,
    MOBILE_OS,
    LISTINGS_TYPE,
    SMART_BANNER,
} from '../constants/enum'
import { isContainNumeric } from '../regex'
import { ReactComponent as StepperActiveIcon } from '../../assets/images/icons/stepper-active.svg'
import { ReactComponent as StepperCheckIcon } from '../../assets/images/icons/stepper-check.svg'
import { ReactComponent as StepperUncheckIcon } from '../../assets/images/icons/stepper-uncheck.svg'
import { ReactComponent as AmericanExpressIcon } from '../../assets/images/icons/cards/american-express.svg'
import { ReactComponent as VisaCardIcon } from '../../assets/images/icons/cards/visa-card.svg'
import { ReactComponent as MasterCardIcon } from '../../assets/images/icons/cards/mastercard.svg'
import { ReactComponent as DefaultCardIcon } from '../../assets/images/icons/cards/default-card.svg'
import { ReactComponent as UnionPayCardIcon } from '../../assets/images/icons/cards/union-pay.svg'
import { ReactComponent as JCBCardIcon } from '../../assets/images/icons/cards/jcb.svg'
import { ReactComponent as DiscoverCardIcon } from '../../assets/images/icons/cards/discover.svg'
import {
    ContentBodyNode,
    PostImage,
    Posts,
    ShopItems,
    VideoCarousel,
} from '../types/ContentStack'
import { Event } from '../types/Endeavor'
import { AddUserGiftPlan, SubscriptionPlan } from '../types/common'
import { EncryptLoginCred } from '../types/auth'
import { userPaymentInit } from '../../services/endeavor'

/**
 * @param condition - boolean that will be checked for response
 * @param truthyClass - class applied when the condition is true
 * @param falsyClass - class applied when the condition is false
 * @param commonClass - classes which are common in both condition
 *
 * Returns a string with the list of classes separated by spaces
 * in a string.
 */
type ConditionalClasses = (
    condition: boolean,
    truthyClass: string,
    falsyClass?: string,
    commonClass?: string
) => string

export const conditionalClasses: ConditionalClasses = (
    condition,
    truthyClass,
    falsyClass = '',
    commonClass = ''
) => {
    return `${commonClass} ${condition ? truthyClass : falsyClass}`
}

export const emailEncryption = (email: string) => {
    const [name, domain] = email.split('@')
    return `${name[0]}${name[1]}${new Array(name.length - 1).join(
        '*'
    )}@${domain}`
}

export const licencePrice = (amount: number, scale: number) => {
    const amountScale = amount / Number(`1${Array(scale).fill('0').join('')}`)
    return amountScale.toFixed(2)
}

export const transformToPost = (item: ContentBodyNode): Posts => {
    return {
        description: item.node.description,
        url: item.node.url,
        category: item.node.category,
        thumbnailConnection: item.node.thumbnailConnection,
        section_imageConnection: item.node.section_imageConnection,
        title: item.node.title,
        free_content: item.node.free_content,
        short_title: item.node.short_title,
    }
}

export const dateConverter = (date: string) => {
    //Here we are getting date as 0225 so we converted as 02/25
    return `${date.substring(0, date.length / 2)}/${date.substring(
        date.length / 2,
        date.length
    )}`
}

/**
 * Click outside custom hook
 */
export const useOutsideClick = <T extends HTMLElement = HTMLElement>(
    ref: React.RefObject<T>,
    callback: (event: MouseEvent | TouchEvent) => void
) => {
    // Using any as no
    const handleClick = (e: MouseEvent | TouchEvent) => {
        if (ref.current && !ref.current.contains(e?.target as Node)) {
            callback(e)
        }
    }

    useEffect(() => {
        document.addEventListener('click', handleClick)
        return () => {
            document.removeEventListener('click', handleClick)
        }
    })
}

export const formatUrlName = (name: string) => {
    return name?.toLowerCase().replace(/\s/g, '-').replaceAll('/', '')
}

export const reverseFormatUrlName = (url: string, capitalize?: boolean) => {
    let response = url.replaceAll('-', ' ')
    if (capitalize) {
        response = response.charAt(0).toUpperCase() + response.slice(1)
    }
    return response
}

export const endPointErrorMessages = (path: string) => {
    switch (path) {
        case 'Pause Subscription':
            return MESSAGES.error.licenceActionNotAllowed('Pause Subscription')
        case 'Resume Subscription':
            return MESSAGES.error.licenceActionNotAllowed('Resume Subscription')
        case 'Cancel Subscription':
            return MESSAGES.error.licenceActionFailed('Cancel Subscription')
        case 'Renewal Subscription':
            return MESSAGES.error.licenceActionFailed('Renewal Subscription')
        case 'Payment Fail':
            return MESSAGES.error.paymentFailError
        default:
            return MESSAGES.error.endpointError
    }
}

export const endPointSuccessMessages = (path: string) => {
    switch (path) {
        case 'Pause Subscription':
            return MESSAGES.success.licenceSuccess('paused')
        case 'Resume Subscription':
            return MESSAGES.success.licenceSuccess('resumed')
        case 'Cancel Subscription':
            return MESSAGES.success.licenceSuccess('canceled for renew')
        case 'Renewal Subscription':
            return MESSAGES.success.licenceSuccess(
                'auto renew after current bill cycle will end.'
            )
        default:
            return MESSAGES.success.requestSuccess
    }
}

export const transformToSplitHit = (data: Array<ShopItems>): Array<Result> => {
    const response: Array<Result> = data.map((item) => {
        const value: Result = {
            uuid: `mwh-code-${item.promo_code}`,
            split_link: item.link.href,
            image_url: item.image?.url ? item.image.url : '',
            name: item.title,
            mwh_code: item.promo_code,
            description: item.description,
        }
        return value
    })
    return response
}

const durationCount = (duration: number, durationPeriod: string) =>
    `${duration} ${durationPeriod}${duration > 1 ? 's' : ''}`

export const iso8601DurationParser = (
    value: string,
    isGiftDuration: boolean
) => {
    if (!isGiftDuration) {
        if (value === 'P1Y') return 'Annual'
        else if (value === 'P1M') return 'Monthly'
    }
    const duration = moment.duration(value)
    let years: string | number = duration.years()
    if (years > 0) years = durationCount(years, 'year')
    else years = ''

    let months: string | number = duration.months()
    if (months > 0) months = durationCount(months, 'month')
    else months = ''

    let days: string | number = duration.days()
    if (days > 0) days = durationCount(days, 'day')
    else days = ''

    return `${years} ${months} ${days}`
}
//We are getting a whole string of licence description from EDV side. so we split description by section.
export const licenceDescriptionSplit = (description: string) =>
    description
        ?.split('- ')
        .map((plan) => plan && <li key={`plan-${plan}`}>{plan}</li>)

export const stepperIcon = (index: number, activeStep: number) => {
    if (activeStep === index) return StepperActiveIcon
    return index < activeStep ? StepperCheckIcon : StepperUncheckIcon
}

export const cardIconRendering = (cardType: string) => {
    switch (cardType) {
        case CARD_TYPE.VISA:
            return <VisaCardIcon />
        case CARD_TYPE.AMERICAN_EXPRESS:
            return <AmericanExpressIcon />
        case CARD_TYPE.MASTERCARD:
            return <MasterCardIcon />
        case CARD_TYPE.JCB:
            return <JCBCardIcon />
        case CARD_TYPE.DISCOVER:
            return <DiscoverCardIcon />
        case CARD_TYPE.UNION_PAY:
            return <UnionPayCardIcon />
        default:
            return <DefaultCardIcon />
    }
}

//check user saved card is expired or not
export const isCardExpired = (month: number, year: number) => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    if (currentYear < year) return false
    if (currentMonth + 1 < month) return false
    return true
}

// check for user saved address state has number in ISOCode
export const userSavedAddressState = (state: string, countryCode: string) => {
    const statesList = State.getStatesOfCountry(countryCode) as IState[]
    const isStateContainsNumericCode = isContainNumeric.test(state)
    if (statesList.length > 0 && isStateContainsNumericCode) {
        const stateName = statesList.find((index) => index.isoCode === state)
        //here we will replace numeric ISOCode to state name.
        if (stateName) return stateName.name
        else return state
    } else {
        return state //If state name is contains alphabets then direct return to state name.
    }
}

export const isUserLoggedIn = () => {
    return (
        localStorage.getItem(AUTHENTICATED_USER.IS_LOGIN_USER) ===
        AUTHENTICATED_USER.IS_AUTHENTICATED_USER
    )
}

export const isValidUrl = (urlString: string) => {
    try {
        if (urlString) return Boolean(new URL(urlString))
        return false
    } catch (e) {
        return false
    }
}

export const getIpAddress = async (): Promise<string | undefined> => {
    try {
        const response = await fetch(GET_IP_ADDRESS_URL)
        const res = await response.json()
        return res?.ip
    } catch (error) {
        return
    }
}

export const convertToFullDate = (date: number): string => {
    const month = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ]
    const m = moment(date)
    return `${month[m.month()]} ${m.date()} at ${m.format('hh:mm A')}`
}

export const transformToSlide = (item: Event): VideoCarousel => {
    return {
        videos: {
            sub_head: item.live ? '' : convertToFullDate(item.startDate),
            title: item.title,
            video_id: item.id.toString(),
            background_imageConnection: {
                edges: [
                    {
                        node: {
                            url: item.thumbnailUrl,
                            title: item.title,
                        },
                    },
                ],
            },
            is_live: item.live,
            startDate: item.startDate,
            endDate: item.endDate,
            isEvent: true,
        },
        __typename: VIDEO_CAROUSEL.VIDEO,
    }
}

export const getShortDay = (value: string): string => {
    switch (value) {
        case 'Monday':
            return 'Mon'
        case 'Tuesday':
            return 'Tue'
        case 'Wednesday':
            return 'Wed'
        case 'Thursday':
            return 'Thu'
        case 'Friday':
            return 'Fri'
        case 'Saturday':
            return 'Sat'
        case 'Sunday':
            return 'Sun'
        default:
            return value
    }
}

// To mask contentstack images url with custom domain
export const maskImagesDomain = (data: any) => {
    if (data && (Array.isArray(data) || typeof data === 'object')) {
        for (let [key, value] of Object.entries(data)) {
            if (Array.isArray(value) || typeof value === 'object') {
                maskImagesDomain(value)
            } else if (
                typeof value === 'string' &&
                value.includes('https://images.contentstack.io')
            ) {
                data[key] = value.replace(
                    'https://images.contentstack.io',
                    IMAGES_DOMAIN
                )
            }
        }
    }
}

export const giftPlanNames = (name: string) => {
    if (name) {
        if (name.toLowerCase().includes('monthly'))
            return name.replace(/monthly/gi, '1 month')
        else if (name.toLowerCase().includes('annual'))
            return name.replace(/annual/gi, '1 year')
    }
    return name
}

export const objectToQueryString = (data: any): string => {
    if (data && Object.keys(data).length > 0)
        return Object.keys(data)
            .map((key) => {
                return (
                    encodeURIComponent(key) +
                    '=' +
                    encodeURIComponent(data[key])
                )
            })
            .join('&')
    return ''
}

export const percentSavingsOnPlan = (plan: SubscriptionPlan): number => {
    const duration = moment.duration(
        plan.licence.purchaseStrategy?.subscriptionPeriod
    )
    if (duration.years() === 1) {
        return 25
    }
    return 0
}

// used to calculate licence price and return origginal, monthly and discount price.
export const calculatePlanAmount = (plan: SubscriptionPlan) => {
    const licenceAmount = Number(
        licencePrice(
            plan?.licence?.amounts[0].amount,
            plan?.licence?.amounts[0].scale
        )
    )
    const discount = licenceAmount % percentSavingsOnPlan(plan)
    const amountObj = {
        original: (licenceAmount + discount).toFixed(2),
        monthly: (licenceAmount / 12).toFixed(2),
        discount: licenceAmount.toFixed(2),
    }
    return amountObj
}

//Date format as MMMM DD, YYYY
export const dateFormat = (date: number) => {
    return `${new Date(date).toLocaleDateString('default', {
        month: 'long',
    })} ${new Date(date).toLocaleDateString('default', {
        day: '2-digit',
    })}, ${new Date(date).toLocaleDateString('default', {
        year: 'numeric',
    })}`
}

export const updateHeaderHeight = () => {
    const promoBanner = document.querySelector('.promo-banner')
    const header = document.querySelector('header')

    document.documentElement.style.setProperty(
        '--promo-banner-height',
        `${promoBanner?.getClientRects()?.[0]?.height ?? 0}px`
    )
    // Needed to add 80px if header is not there because css is adjusted keeping in mind that container will have 80px padding
    document.documentElement.style.setProperty(
        '--header-height',
        `${header?.getClientRects()?.[0]?.height ?? 80}px`
    )
    document.documentElement.style.setProperty(
        '--promo-header-height',
        `${
            (header?.getClientRects()?.[0]?.height ?? 80) +
            (promoBanner?.getClientRects()?.[0]?.height ?? 0)
        }px`
    )
}

export const specificDeviceImage = (state: PostImage) => {
    const device = window.outerWidth
    if (
        device <= 768 &&
        device > 425 &&
        state.image.tablet_imageConnection.edges.length > 0
    ) {
        return {
            url: state.image.tablet_imageConnection.edges[0].node!.url,
            title: state.image.tablet_imageConnection.edges[0].node?.title
                ? state.image.tablet_imageConnection.edges[0].node?.title
                : '',
        }
    }
    if (device <= 425 && state.image.mobile_imageConnection.edges.length > 0) {
        return {
            url: state.image.mobile_imageConnection.edges[0].node!.url,
            title: state.image.mobile_imageConnection.edges[0].node?.title
                ? state.image.mobile_imageConnection.edges[0].node?.title
                : '',
        }
    }
    if (
        device > 1028 ||
        state.image.mobile_imageConnection.edges.length === 0 ||
        state.image.tablet_imageConnection.edges.length === 0
    ) {
        return {
            url: state.image.imageConnection.edges[0].node!.url,
            title: state.image.imageConnection.edges[0].node?.title
                ? state.image.imageConnection.edges[0].node?.title
                : '',
        }
    }
}

/**
 * Determine the mobile operating system.
 * This function returns one of 'ios', or 'android'
 *
 * @returns {String}
 */
export const getMobileOS = (): string => {
    const userAgent = navigator.userAgent || (window as any).opera

    if (/android/i.test(userAgent)) {
        return MOBILE_OS.ANDROID
    }

    return MOBILE_OS.IOS
}

export const getKebabCaseSentence = (args: string) =>
    args
        .split(' ')
        .map((item) => item.toLowerCase().trim())
        .join('-')

export const hyphenToTitleCase = (str: string) => {
    return str.split('-').join(' ')
}

export const encryptJSON = (
    json: EncryptLoginCred,
    shift: number,
    key: string
) => {
    const jsonString = JSON.stringify(json)
    let encryptedJSON = ''
    for (let i = 0; i < jsonString.length; i++) {
        const charCode = jsonString.charCodeAt(i)
        if (charCode >= 65 && charCode <= 90) {
            encryptedJSON += String.fromCharCode(
                ((charCode - 65 + shift) % 26) + 65
            ) // Uppercase letters
        } else if (charCode >= 97 && charCode <= 122) {
            encryptedJSON += String.fromCharCode(
                ((charCode - 97 + shift) % 26) + 97
            ) // Lowercase letters
        } else {
            encryptedJSON += jsonString.charAt(i) // Non-alphabetic characters
        }
    }

    // Add authentication key to the encrypted JSON
    const encryptedData = {
        data: encryptedJSON,
        key: key,
    }

    return btoa(JSON.stringify(encryptedData)) // Encode the encrypted data as base64
}

export const decryptJSON = (
    encryptedData: string,
    shift: number,
    key: string
) => {
    const decodedData = atob(encryptedData) // Decode the base64 encoded encrypted data
    const encryptedJSON = JSON.parse(decodedData).data

    // Check if the authentication key matches
    if (JSON.parse(decodedData).key !== key) {
        throw new Error('Invalid authentication key')
    }

    let decryptedJSON = ''
    for (let i = 0; i < encryptedJSON.length; i++) {
        const charCode = encryptedJSON.charCodeAt(i)
        if (charCode >= 65 && charCode <= 90) {
            decryptedJSON += String.fromCharCode(
                ((charCode - 65 - shift + 26) % 26) + 65
            ) // Uppercase letters
        } else if (charCode >= 97 && charCode <= 122) {
            decryptedJSON += String.fromCharCode(
                ((charCode - 97 - shift + 26) % 26) + 97
            ) // Lowercase letters
        } else {
            decryptedJSON += encryptedJSON.charAt(i) // Non-alphabetic characters
        }
    }
    return JSON.parse(decryptedJSON)
}

/**
 *
 * Generates a  link based on the provided listing type and item.
 *
 * @param {string} listingType - The type of listing (e.g., "creator", "video_category").
 * @param {ContentBodyNode} item - The item containing bodynode .
 * @returns {string} The generated program link.
 */
export const generateProgramLink = (
    listingType: string,
    item: ContentBodyNode
): string => {
    if (item.node.section_view && item.node.section_name) {
        return `/program/${formatUrlName(item.node.section_name)}`
    } else if (listingType === LISTINGS_TYPE.CREATOR) {
        return `/creator/${item.node.playlist_id}/${formatUrlName(
            item.node.title
        )}`
    } else {
        return `/category/${item.node.playlist_id}/${formatUrlName(
            item.node.title
        )}`
    }
}

export const getSmartBannerClosedValue = () => {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=')
        if (name === SMART_BANNER.COOKIES) {
            return value
        }
    }
    return null
}

export const getPluralize = (
    count: number,
    noun: string,
    suffix = 's',
    hideCount = false
) => {
    return `${!hideCount ? count : ''} ${noun}${count !== 1 ? suffix : ''}`
}

export const calculateMonthDays = (data: number) => {
    let years = 0,
        months = 0,
        days = 0
    while (data) {
        if (data >= 365) {
            years++
            data -= 365
        } else if (data >= 30) {
            months++
            data -= 30
        } else {
            days++
            data--
        }
    }

    const daysData = `${days} day${days !== 1 ? 's' : ''}`
    const monthData = `${months} month${months !== 1 ? 's' : ''}`
    const yearData = `${years} year${years !== 1 ? 's' : ''}`

    // display years only if more than 0 otherwise just display month and day.
    if (years > 0) {
        return `${yearData}, ${monthData} and ${daysData}`
    }
    // display months only if more than 0 otherwise just display day.
    if (months > 0) {
        return `${monthData} and ${daysData}`
    } else {
        return daysData
    }
}

export const inBetweenDates = (startDate: string, endDate: string) => {
    const convertedStartDate = new Date(startDate)
    const convertedEndDate = new Date(endDate)
    let todayDate = new Date()
    return convertedStartDate <= todayDate && todayDate <= convertedEndDate
}

export const giftUserPaymentInit = async (
    postUserGiftPlan: AddUserGiftPlan
) => {
    const {
        total: { amount, currency, scale },
    } = postUserGiftPlan
    const { targetPaymentProvider, paymentProviderType } =
        postUserGiftPlan?.availablePaymentProviders[0]
    const paymentInit = {
        primaryPaymentDetails: {
            targetPaymentProvider,
            paymentProviderType,
            paymentMethodDetails: {
                paymentMethodType: 'CARD',
                amount: {
                    currency: currency,
                    amount: amount,
                    scale: scale,
                },
            },
        },
    }
    const stripeData = await userPaymentInit(paymentInit)
    return stripeData
}

export const GiftPurchaseDetails = () => {
    return (
        <div className="membership-includes">
            <ul className="tick-checklist">
                {giftLicenseDetails.map((desc) => (
                    <li key={`plan-${desc}`}>{desc}</li>
                ))}
            </ul>
        </div>
    )
}

export const trimSearchQuery = (query: string) => {
    const value = query
        .replace(/[^A-Za-z0-9 ]/g, '') // Remove all non-alphanumeric characters. Ex. '!@#$%Search^&*()' -> 'Search'
        .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space. Ex. 'A    B' -> 'A B'
        .trim() // Remove any extra starting or ending spaces. Ex. ' A B ' -> 'A B'

    return value
}

export const mwhConvertUppercase = (data: string) => {
    const value = data?.toLocaleLowerCase()
    const checkWord = value?.includes('mwh')
    return checkWord ? value?.replace('mwh', 'MWH') : data
}
